import { type ComponentProps, useCallback, useContext } from "react";
import type { TransactionView } from "@common/types/views/transactionViews";
import { PaginatedTransactionsDataContext } from "@components/transactions/PaginatedTransactionsTable.context";
import { TransactionsView } from "@components/transactions/views/TransactionsView";
import { restoreHiddenColumns } from "../restoreHiddenColumns";
import { useConditionallyHiddenColumns } from "../useConditionallyHiddenColumns";
import { useTransactionRowChatDrag } from "./useTransactionRowChatDrag";

/**
 * The task's transactions table with rows draggable into the task chat.
 * Renders inside `TransactionsBulkActionsContextProvider`, which holds the selection.
 * Leaves out the Lock and Split columns while no loaded row needs them, and
 * puts them back into every view change it reports.
 */
export const TransactionsTaskView = (
    props: ComponentProps<typeof TransactionsView>,
) => {
    const { view, transformViewChange } = props;
    const rowDragOut = useTransactionRowChatDrag();
    const { transactions } = useContext(PaginatedTransactionsDataContext);
    const renderedView = useConditionallyHiddenColumns(view, transactions);

    const restoreAndTransformViewChange = useCallback(
        (changedView: TransactionView) => {
            const restoredView = {
                ...changedView,
                columns: restoreHiddenColumns({
                    changedColumns: changedView.columns,
                    fullColumns: view.columns,
                }),
            };
            return transformViewChange
                ? transformViewChange(restoredView)
                : restoredView;
        },
        [view.columns, transformViewChange],
    );

    return (
        <TransactionsView
            {...props}
            view={renderedView}
            transformViewChange={restoreAndTransformViewChange}
            rowDragOut={rowDragOut}
        />
    );
};
